import { useState, useRef, useEffect } from 'react';
import { ArrowDown, ArrowUp, Spinner } from '../../assets/icon/svg';

export interface DropdownOptionValue {
    id: number;
    name: string;
    subTitle?: string;
    value?: number;
    email?: string;
}

interface Props {
    value: DropdownOptionValue | null;
    options: DropdownOptionValue[];
    onChange: (value: DropdownOptionValue) => void;
    width?: string;
    padding?: string;
    open: boolean;
    setOpen: (value: boolean) => void;
    isLoading?: boolean;
    isSubTitle?: boolean;
    isDisable?: boolean;
    isNoBorder?: boolean;
    isShowArrow?: boolean;
    minH?: string;
    hasSubtitle?: boolean;
    isError?: boolean;
    errorMessage?: string;
}

export default function DropdownOption({ isError = false, errorMessage = 'Unable to load options.', hasSubtitle = false, minH = 'min-h-[34px]', isShowArrow = true, isDisable, isNoBorder = false, isSubTitle = false, isLoading, open, setOpen, value, onChange, options, width = 'w-1/3', padding = 'px-3 py-2' }: Props) {
    
    const selected = value || null;
    const [dropUp, setDropUp] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open || !buttonRef.current) return;

        const calculatePosition = () => {
            const rect = buttonRef.current!.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = Math.min(options.length * 42, 200);

            if (spaceBelow < dropdownHeight + 10 && spaceAbove > spaceBelow) {
                setDropUp(true);
            } else {
                setDropUp(false);
            }
        };

        calculatePosition();

        window.addEventListener('scroll', calculatePosition);
        window.addEventListener('resize', calculatePosition);

        return () => {
            window.removeEventListener('scroll', calculatePosition);
            window.removeEventListener('resize', calculatePosition);
        };
    }, [open, options.length]);

    const getButtonStyle = () => {
        if (isError) {
            return 'rounded-md border border-red-300 bg-red-50';
        }

        if (isNoBorder) {
            return 'bg-gray-100 rounded-md border border-gray-300';
        }

        if (open) {
            return 'rounded-md border border-[#186cb2] bg-white ring-1 ring-[#186cb2]/20';
        }

        if (value?.name === 'None' || value?.name === 'Select') {
            return 'rounded-md border border-gray-300 bg-white';
        }

        return 'rounded-md border border-[#186cb2]/30 bg-[#186cb2]/10';
    };

    const isSelected = (option: DropdownOptionValue) => { return selected?.id === option.id; };

    return (
        <div className={`relative ${width}`}>
            <button disabled={isDisable} ref={buttonRef} className={`disabled:cursor-not-allowed w-full ${minH} text-xs flex justify-between items-center ${getButtonStyle()} ${padding}`} onClick={() => setOpen(!open)}>
                <div className={`min-w-0 flex-1 text-left ${isShowArrow ? 'mr-2' : ''}`}>
                    <div className="truncate text-xs text-gray-700">
                        {selected?.name}
                    </div>
                    {selected?.id !== 0 && hasSubtitle && selected?.subTitle && (
                        <div className="truncate text-[10px] text-gray-500">
                            {selected.subTitle}
                        </div>
                    )}
                </div>
                {/* <span className={`flex-1 text-left truncate ${isShowArrow ? 'mr-2' : ''}`}>{selected?.name}</span> */}
                {isShowArrow && (
                    <span className="flex items-center justify-center w-4 h-4 flex-shrink-0 text-gray-500">
                        {open ? <ArrowUp /> : <ArrowDown />}
                    </span>
                )}
            </button>

            {open && isShowArrow && (
                <div className={`absolute z-50 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-40 overflow-y-auto ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`} style={{ bottom: dropUp ? '100%' : undefined, top: dropUp ? undefined : '100%', marginTop: dropUp ? '0px' : '5px', marginBottom: dropUp ? '5px' : '0px' }}>
                    {isLoading ? (
                        <div className="flex h-20 w-full flex-col items-center justify-center gap-1.5">
                            <Spinner color="#186cb2" size={20} />
                            <span className="text-[10px] text-gray-400">
                                Loading options...
                            </span>
                        </div>
                    ) : isError ? (
                        <div className="flex h-20 w-full flex-col items-center justify-center gap-1 px-3 text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 text-red-500" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.007M10.29 3.86l-7.5 13a1.5 1.5 0 001.3 2.25h15.82a1.5 1.5 0 001.3-2.25l-7.5-13a1.5 1.5 0 00-2.6 0z" />
                            </svg>
                            <span className="text-[10px] text-red-500">
                                {errorMessage}
                            </span>
                        </div>
                    ) : options.length === 0 ? (
                        <div className="flex h-20 w-full items-center justify-center px-3 text-center">
                            <span className="text-[10px] text-gray-400">
                                No options available.
                            </span>
                        </div>
                    ) : (
                        options.map((option) => {
                            const selectedOption = isSelected(option);
                            return (
                                <button
                                    type="button"
                                    key={option.id}
                                    onClick={() => {
                                        onChange(option);
                                        setOpen(false);
                                    }}
                                    className={`flex w-full items-center justify-between px-3 py-2 text-left transition-colors ${
                                        selectedOption ? "bg-[#186cb2]/10" : "hover:bg-[#186cb2]/5"
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className={`truncate text-xs ${selectedOption ? "font-medium text-[#186cb2]" : "text-gray-700" }`} >
                                            {option.name}
                                        </div>
                                        {option.id !== 0 && hasSubtitle && (
                                            <div className="truncate text-[10px] text-gray-500">
                                                {option.subTitle}
                                            </div>
                                        )}
                                    </div>
                                    {selectedOption && (
                                        <span className="ml-3 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 shrink-0 text-[#186cb2]" >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                                            </svg>
                                        </span>
                                    )}
                                </button>
                            )
                        })
                    )}
                </div>
            )}
        </div>
    );
}